<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('why')
import { onMounted } from 'vue'
import HeroVideo from '@/assets/background.mp4'
import genImages from '@/generated/gen-images.json'
import HalftoneVeil from '@/components/HalftoneVeil.vue'

import {
  QrCodeIcon,
  ChartBarIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ChevronDownIcon
} from '@heroicons/vue/24/outline'

// Measure section — copy lives in i18n (why.measure.items), icons here
const measureIcons = [
  QrCodeIcon,
  ChartBarIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  ArrowDownTrayIcon
]

const scrollToContent = () => {
  document.getElementById('organisation')?.scrollIntoView({ behavior: 'smooth' })
}

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden bg-slate-950">

    <!-- Video background + sober dark overlay -->
    <div class="absolute inset-0">
      <video autoplay loop muted playsinline class="absolute inset-0 w-full h-full object-cover">
        <source :src="HeroVideo" type="video/mp4" />
      </video>
      <div class="absolute inset-0 bg-slate-950/70"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
        <div
          class="inline-flex items-center px-3 py-1.5 border border-white/15 text-slate-300 text-xs font-mono tracking-widest uppercase mb-10 rounded-md">
          <span class="flex h-1.5 w-1.5 rounded-full bg-primary-400 me-2"></span>
          {{ $t('why.hero.badge') }}
        </div>

        <h1 class="text-4xl md:text-6xl font-semibold text-white leading-[1.1] max-w-4xl mx-auto">
          {{ $t('why.hero.titleMain') }}<br />
          <span class="text-primary-400">{{ $t('why.hero.titleHighlight') }}</span>
        </h1>

        <p class="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-slate-300 leading-relaxed">
          {{ $t('why.hero.desc') }}
        </p>

        <div class="mt-10 flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition">
            {{ $t('why.hero.ctaDemo') }}
          </a>
          <button @click="scrollToContent"
            class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition flex items-center justify-center gap-2">
            {{ $t('why.hero.ctaDiscover') }}
            <ChevronDownIcon class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- ================= ORGANISATION : BEFORE / AFTER ================= -->
  <section id="organisation" class="py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('why.org.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('why.org.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('why.org.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <template v-for="i in 4" :key="i">
          <!-- Before -->
          <div class="bg-slate-50 p-8">
            <div class="flex items-center justify-between mb-5">
              <span class="text-[11px] font-mono text-slate-400 tracking-widest uppercase">
                {{ $t(`why.org.rows[${i - 1}].tag`) }}
              </span>
              <span class="text-[11px] font-mono text-slate-400 border border-slate-200 rounded px-2 py-0.5">
                {{ $t('why.org.badgeBefore') }}
              </span>
            </div>
            <p class="text-sm text-slate-500 leading-relaxed">{{ $t(`why.org.rows[${i - 1}].before`) }}</p>
          </div>
          <!-- After -->
          <div class="bg-white p-8">
            <div class="flex items-center justify-between mb-5">
              <span class="text-[11px] font-mono text-slate-400 tracking-widest uppercase">
                {{ $t(`why.org.rows[${i - 1}].tag`) }}
              </span>
              <span class="text-[11px] font-mono text-primary-600 border border-primary-200 rounded px-2 py-0.5">
                {{ $t('why.org.badgeAfter') }}
              </span>
            </div>
            <p class="text-sm text-slate-700 leading-relaxed">{{ $t(`why.org.rows[${i - 1}].after`) }}</p>
          </div>
        </template>
      </div>
    </div>
  </section>

  <!-- ================= SHOWCASE — mur de données ================= -->
  <section class="bg-slate-950">
    <div class="relative overflow-hidden aspect-[3/1] reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
      <img :src="genImages['why-measure-datawall']"
        alt="Mur d'écrans d'analytics affichant courbes de fréquentation et carte lumineuses, dans une régie d'événement"
        class="w-full h-full object-cover" loading="lazy" />
      <!-- Raccord haut (section claire) : trame demi-teinte -->
      <HalftoneVeil color="#f8fafc" side="top" class="absolute inset-0" />
      <!-- Raccord bas (section sombre) : fondu discret côté nuit -->
      <div class="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-slate-950 to-transparent"></div>
    </div>
  </section>

  <!-- ================= MEASURE : EVENTS AS DATA ================= -->
  <section class="py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('why.measure.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold mb-5">{{ $t('why.measure.title') }}</h2>
        <p class="text-lg text-slate-400">{{ $t('why.measure.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in measureIcons" :key="i" class="bg-slate-950 p-8">
          <component :is="icon" class="w-7 h-7 text-primary-400 mb-6" />
          <h3 class="text-lg font-semibold text-white mb-2">{{ $t(`why.measure.items[${i}].title`) }}</h3>
          <p class="text-sm text-slate-400 leading-relaxed">{{ $t(`why.measure.items[${i}].desc`) }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= COMPARISON TABLE ================= -->
  <section class="py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('why.compare.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('why.compare.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('why.compare.desc') }}</p>
      </div>

      <div
        class="overflow-x-auto bg-white border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <table class="w-full min-w-[720px] text-sm">
          <thead>
            <tr class="border-b border-slate-200 bg-slate-50">
              <th class="px-6 py-4 text-start text-xs font-mono tracking-widest uppercase text-slate-500 font-medium">
                {{ $t('why.compare.colCapability') }}
              </th>
              <th class="px-6 py-4 text-start text-xs font-mono tracking-widest uppercase text-slate-500 font-medium">
                {{ $t('why.compare.colStandard') }}
              </th>
              <th class="px-6 py-4 text-start text-xs font-mono tracking-widest uppercase text-primary-600 font-medium">
                {{ $t('why.compare.colBifrost') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200">
            <tr v-for="i in 7" :key="i">
              <td class="px-6 py-5 font-semibold text-slate-900 align-top whitespace-nowrap">
                {{ $t(`why.compare.rows[${i - 1}].label`) }}
              </td>
              <td class="px-6 py-5 text-slate-500 align-top">
                {{ $t(`why.compare.rows[${i - 1}].standard`) }}
              </td>
              <td class="px-6 py-5 text-slate-700 align-top">
                <span class="flex items-start">
                  <CheckIcon class="w-4 h-4 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
                  {{ $t(`why.compare.rows[${i - 1}].bifrost`) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <!-- ================= SHOWCASE — régie / coordination ================= -->
  <!-- Placé entre deux sections claires (comparatif slate-50 / app terrain
       blanc) : le bloc image casse la continuité claire avant le field app. -->
  <section class="bg-slate-950">
    <div class="relative overflow-hidden aspect-[3/1] reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
      <img :src="genImages['why-org-command']"
        alt="Poste de coordination en régie d'événement, ordinateurs portables et casques, équipe vue de dos"
        class="w-full h-full object-cover" loading="lazy" />
      <!-- Raccord haut (comparatif slate-50) : trame demi-teinte -->
      <HalftoneVeil color="#f8fafc" side="top" class="absolute inset-0" />
      <!-- Raccord bas (section blanche) : trame demi-teinte -->
      <HalftoneVeil color="#ffffff" side="bottom" class="absolute inset-0" />
    </div>
  </section>

  <!-- ================= FIELD APP (hostess & manager) ================= -->
  <section class="py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('why.field.label') }}</p>
          <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('why.field.title') }}</h2>
          <p class="text-lg text-slate-500 mb-6">{{ $t('why.field.description') }}</p>
          <span
            class="inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-md text-[11px] font-mono text-slate-500 tracking-wide">
            {{ $t('why.field.tag') }}
          </span>
        </div>
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div class="rounded-lg overflow-hidden border border-slate-200 mb-6">
            <img :src="genImages['why-field-checkin']"
              alt="Scan d'un badge QR au comptoir d'accueil, à l'entrée d'un événement"
              class="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
          </div>
          <ul class="space-y-4">
            <li v-for="i in 4" :key="i" class="flex items-start text-sm text-slate-600">
              <CheckIcon class="w-4 h-4 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`why.field.points[${i - 1}]`) }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-slate-950 rounded-lg p-12 md:p-20 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6">{{ $t('why.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
          {{ $t('why.cta.desc') }}
        </p>
        <a href="https://bifrost.events/touch" target="_blank"
          class="inline-block px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
          {{ $t('why.cta.button') }}
        </a>
      </div>
    </div>
  </section>
</template>

<style>
/* Reveal-on-scroll: reset transforms once the IntersectionObserver fires */
</style>
